import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Stack, Button } from '@mui/material';

import { useClientListUserTokens } from '@apono/client-app-sdk';

import routes from '@routes';

import { Loader } from '@common/ui/Loader';

import { EmptyState } from '@common/components/EmptyState';
import { SearchBar } from '@common/components/SearchBar';
import TokensTable from '@PersonalApiTokens/components/TokensTable';
import TokensTableRow from '@PersonalApiTokens/components/TokensTableRow';
import TokensTableRowAction from './TokensTableRowAction';

const FILTER_QP = 'filter';

export default function PersonalTokensTable() {
  const { data: tokens, isFetched } = useClientListUserTokens();

  const [searchParams, setSearchParams] = useSearchParams();

  const filter = searchParams.get(FILTER_QP) || '';

  const handleFilterChange = (val: string) => {
    searchParams.set(FILTER_QP, val);
    setSearchParams(searchParams);
  };

  const filteredTokens = useMemo(
    () =>
      tokens.filter((token) => {
        const filterText = filter.toLowerCase();

        const foundByName = token.name.toLowerCase().includes(filterText);

        const prefix = filterText.slice(0, 4);
        const suffix = filterText.slice(-4);
        const foundByToken = token.masked_token.startsWith(prefix) && token.masked_token.endsWith(suffix);

        return foundByName || foundByToken;
      }),
    [tokens, filter],
  );

  if (!isFetched) {
    return <Loader />;
  }

  if (tokens.length === 0) {
    return (
      <EmptyState
        imgSrc="/static/illustrations/not-found.svg"
        body="Apono API Tokens can be used for integration with external tools and working with the Apono API. Tokens are
      specific to users."
        action={
          <Button
            component={Link}
            to={routes.AddPersonalApiToken.path}
            data-testid="add-api-token-button"
            variant="contained"
          >
            Create Token
          </Button>
        }
      />
    );
  }

  return (
    <Stack spacing={2}>
      <SearchBar total={filteredTokens.length} value={filter} onChange={handleFilterChange} />
      <TokensTable>
        {filteredTokens.map((token) => (
          <TokensTableRow key={token.id} token={token} action={<TokensTableRowAction token={token} />} />
        ))}
      </TokensTable>
    </Stack>
  );
}
